import type { ReactNode } from 'react';
import { cn } from '@tempmee/core';
import { Column, Typography } from '@tempmee/react';

export interface IShiftDetailsPanelProps {
  /** Section heading, e.g. "Candidates". Omit for a panel the design leaves untitled. */
  title?: ReactNode;
  children: ReactNode;
  /** Extra classes on the panel surface — a min-height, for instance. */
  className?: string;
  'data-testid'?: string;
  'data-cy'?: string;
}

/**
 * The white panel the redesigned Shift Details pages put their primary column
 * in — Figma's `Atom/SectionHeadings` over content, on the blocks' own surface.
 *
 * It matches the surface `AdditionalDetails` and `Receipt` declare for
 * themselves (`rounded-lg bg-background`, 12px padding) so the primary column and
 * the sidebar read as one system. The classes resolve because
 * `@tempmee/react-blocks/styles.css` already emits them for those blocks —
 * dental-portal has no Tailwind build of its own.
 *
 * The heading reproduces the blocks' internal `SectionHeading`, which is not a
 * public export: with no action and no icon that component degenerates to this
 * single bold `bodyLarge` in `text-secondary`. `as="h2"` is load-bearing —
 * `bodyLarge` renders as a paragraph by default, which would stop the section
 * from being a heading for screen readers under the page's own H1.
 */
export const ShiftDetailsPanel = ({
  title,
  children,
  className,
  'data-testid': testId,
  'data-cy': dataCy,
}: IShiftDetailsPanelProps) => (
  <Column
    gap={4}
    p={3}
    className={cn('w-full rounded-lg bg-background', className)}
    data-testid={testId}
    data-cy={dataCy}
  >
    {title != null ? (
      <Typography variant="bodyLarge" bold as="h2" className="text-text-secondary">
        {title}
      </Typography>
    ) : null}
    {children}
  </Column>
);
